import { httpsCallable } from 'firebase/functions';
import { getAuthInstance, getAppCheckToken, functions } from '../firebase.js';

// Helper function to get Firebase Auth token
async function getAuthToken() {
  try {
    const auth = await getAuthInstance();
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user');
    }
    return await user.getIdToken();
  } catch (error) {
    console.error('Error getting auth token:', error);
    throw error;
  }
}

const RETRYABLE_STATUS_CODES = [500, 502, 503, 504];

/**
 * Make authenticated API calls with App Check token and automatic retry for transient failures.
 */
async function authenticatedFetch(url, options = {}, _attempt = 0) {
  try {
    const [token, appCheckToken] = await Promise.all([
      getAuthToken(),
      getAppCheckToken()
    ]);

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    };

    if (appCheckToken) {
      headers['X-Firebase-AppCheck'] = appCheckToken;
    }

    const response = await fetch(url, {
      ...options,
      headers
    });
    
    if (!response.ok) {
      if (RETRYABLE_STATUS_CODES.includes(response.status) && _attempt < 1) {
        console.warn(`API returned ${response.status}, retrying...`);
        await new Promise(resolve => setTimeout(resolve, 1500));
        return authenticatedFetch(url, options, _attempt + 1);
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    if (error.name === 'TypeError' && _attempt < 1) {
      console.warn('Network error, retrying...');
      await new Promise(resolve => setTimeout(resolve, 1500));
      return authenticatedFetch(url, options, _attempt + 1);
    }
    console.error('API call failed:', error);
    throw error;
  }
}

export { authenticatedFetch };

/**
 * Call a Firebase Callable Function by name.
 * Auth, App Check, CORS, and JSON serialization are handled automatically by the SDK.
 */
export async function callFunction(name, data = {}, options = {}) {
  const fn = httpsCallable(functions, name, options);
  const result = await fn(data);
  return result.data;
}

/**
 * Call a streaming Firebase Callable Function (httpsCallable(...).stream()).
 * Invokes `onChunk(text)` for each streamed text delta and resolves to the
 * function's final return value (same shape as callFunction).
 *
 * If the backend/SDK can't stream, the server sends the whole answer as a
 * single chunk, so `onChunk` still fires once and the final data is returned.
 *
 * @param {string} name Callable function name.
 * @param {object} data Request payload.
 * @param {(text: string) => void} onChunk Called with each text delta.
 * @param {object} options httpsCallable options (e.g. { timeout }).
 */
export async function streamFunction(name, data = {}, onChunk, options = {}) {
  const fn = httpsCallable(functions, name, options);
  const { stream, data: finalData } = await fn.stream(data);
  for await (const chunk of stream) {
    const text = typeof chunk === 'string' ? chunk : chunk?.text;
    if (typeof text === 'string' && text.length > 0 && typeof onChunk === 'function') {
      onChunk(text);
    }
  }
  return await finalData;
}

// Property API calls
export const propertyAPI = {
  searchNearby: (params) => callFunction('propertySearch', params),
  searchSold: (params) => callFunction('soldProperties', params),
  searchByAddress: (params) => callFunction('propertyByAddress', params),
  searchById: (listingId) => callFunction('propertyById', { listing_id: listingId }),
  searchByZip: (zip) => callFunction('propertyByZip', { zip }),
  searchActive: (params) => callFunction('activeProperties', params),
  searchByMls: (mlsNumber) => callFunction('mlsById', { mls_number: mlsNumber }),
};

// CMA API calls
export const cmaAPI = {
  generateCMA: (params) => callFunction('cmaAnalysis', params, { timeout: 540000 }),
};

// Mortgage API calls
export const mortgageAPI = {
  calculate: (params) => callFunction('mortgageCalculate', params),
};

// Tax API calls
export const taxAPI = {
  getTaxInfo: (params) => callFunction('propertyTax', params),
};

// PropMix API calls
export const propmixAPI = {
  analyze: (params) => callFunction('propmixAnalyze', params),
};

export function fetchScatterplotProperties(filters) {
  return callFunction('scatterplotProperties', filters);
}
